import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'
import { ResolvedOnDemandCity } from 'src/constants/onDemandCoverage'
import { PillarPage } from 'src/constants/searchPages'
import { numberWithCommas } from 'src/utils'
import {
  getOnDemandImage,
  hasAnyOnDemandCoverage,
  resolveOnDemandCity
} from 'src/utils/onDemand'
import ga from 'src/utils/pixels/gtag'

interface CrossLinkOnDemandProps {
  /** Pillar of the page rendering this section. The section ONLY renders on
   *  the SelfStorage pillar — Parking, Warehouse, and Industrial pillars
   *  always return null. */
  pillar: PillarPage
  /** Current city slug for the page (e.g. `monterrey`, `polanco`, `cdmx`).
   *  When provided, the section resolves the slug to its on-demand market and
   *  renders the city-aware variant only if `partnerActive: true`. When
   *  undefined (pillar root `/renta-mini-bodegas`), the section renders the
   *  generic variant if at least one market has coverage. */
  currentCity?: string
}

/** Resolution result for the cross-link section. `null` when the section
 *  should not render at all (wrong pillar, no coverage, or unresolved city).
 *  When `resolved` is `null` the generic pillar-root variant should render;
 *  when `resolved` is non-null the city-aware variant should render. */
export type CrossLinkOnDemandResolution = {
  resolved: ResolvedOnDemandCity | null
} | null

/** Pure gating + variant-selection helper. Exposed for tests; the component
 *  reads its own props and forwards them here.
 *
 *  Returns:
 *   - `null` when the section should not render (non-SelfStorage pillar,
 *     unresolved/inactive city, or pillar-root with zero active coverage).
 *   - `{ resolved: null }` for the generic pillar-root variant.
 *   - `{ resolved: <ResolvedOnDemandCity> }` for the city-aware variant. */
export function resolveCrossLinkOnDemand(
  pillar: PillarPage,
  currentCity: string | undefined
): CrossLinkOnDemandResolution {
  if (pillar !== PillarPage.SelfStorage) return null

  const hasCity = typeof currentCity === 'string' && currentCity.length > 0

  if (hasCity) {
    const resolved = resolveOnDemandCity(currentCity)
    if (!resolved || !resolved.partnerActive) return null
    return { resolved }
  }

  if (!hasAnyOnDemandCoverage()) return null
  return { resolved: null }
}

/**
 * Editorial cross-link section on the SelfStorage pillar that points renters
 * toward the Almacenamiento a Domicilio (door-to-door storage) service.
 *
 * Visibility:
 *  - Pillar root (no `currentCity`): renders generic variant when at least
 *    one on-demand market has `partnerActive: true`. Links to the parent
 *    on-demand landing page.
 *  - City / hood pages: resolves `currentCity` via `resolveOnDemandCity()`.
 *    Renders the city-aware variant only when the resolved market has
 *    `partnerActive: true`. Links to that market's on-demand sub-page.
 *  - Non-SelfStorage pillars: never renders.
 *
 * Editorial vocabulary: cream-300 ground, two-column card with hairline
 * border, delivery-van photo on the left rail, Fraunces eyebrow + H2,
 * blue-500 italic-style accents, blue-500 pill CTA.
 */
const CrossLinkOnDemand = ({ pillar, currentCity }: CrossLinkOnDemandProps) => {
  const { t } = useTranslation('page_mini_bodega')

  const resolution = resolveCrossLinkOnDemand(pillar, currentCity)
  if (!resolution) return null
  const { resolved } = resolution

  const destinationUrl = resolved
    ? `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio/${resolved.config.canonicalSlug}`
    : `/${PillarPage.SelfStorage}/almacenamiento-a-domicilio`

  const title = resolved
    ? t('CROSS_LINK_ON_DEMAND.TITLE_CITY', {
        city: resolved.config.displayName
      })
    : t('CROSS_LINK_ON_DEMAND.TITLE')

  return (
    <section className="border-t border-ink-100/15 bg-cream-300 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="overflow-hidden rounded-lg border border-ink-100/15 bg-white-500">
          <div className="flex flex-col md:grid md:grid-cols-12 md:items-stretch">
            {/* Image — first on mobile, left rail on desktop */}
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-blue-500/10 md:col-span-5 md:aspect-auto md:min-h-[280px]">
              <Image
                src={getOnDemandImage(resolved?.city)}
                alt={t('CROSS_LINK_ON_DEMAND.EYEBROW')}
                width={760}
                height={475}
                sizes="(max-width: 900px) 100vw, 40vw"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>

            <div className="flex flex-col gap-4 p-6 md:col-span-7 md:justify-center md:gap-5 md:p-10">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('CROSS_LINK_ON_DEMAND.EYEBROW')}
              </p>
              <h2 className="font-display text-[22px] leading-[1.2] font-medium text-ink-700 md:text-[28px]">
                {title}
              </h2>
              <p className="max-w-[540px] text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                {t('CROSS_LINK_ON_DEMAND.BODY')}
              </p>
              {/* Price chip — same italic Fraunces vocabulary as the
                  landing-page hero. Renders only when the page has resolved
                  to a covered city (the pillar-root variant has no city
                  pricing to anchor against). */}
              {resolved && (
                <div className="inline-flex w-fit flex-col gap-0.5 rounded-lg border border-blue-500/20 bg-cream-300 px-4 py-3">
                  <span className="font-display text-[20px] leading-none font-medium tracking-[-0.01em] text-blue-500 italic md:text-[24px]">
                    {t('CROSS_LINK_ON_DEMAND.PRICE_FROM', {
                      price: numberWithCommas(
                        resolved.config.perSqmRates.twelveMonths
                      )
                    })}
                  </span>
                  <span className="text-[12px] leading-tight text-ink-200 md:text-[13px]">
                    {t('CROSS_LINK_ON_DEMAND.PRICE_NOTE')}
                  </span>
                </div>
              )}
              <div className="mt-1">
                <Link
                  prefetch={false}
                  href={destinationUrl}
                  onClick={() =>
                    ga.event('pillar_crosslink_click', {
                      to: 'on_demand',
                      from_pillar: pillar,
                      ...(resolved ? { city: resolved.city } : {})
                    })
                  }
                  className="inline-flex items-center justify-center rounded-full bg-blue-500 px-6 py-3 text-base font-semibold text-white-500 transition-all hover:bg-blue-600 active:scale-[0.98]"
                >
                  {t('CROSS_LINK_ON_DEMAND.CTA')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(CrossLinkOnDemand)
